import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import hljs from 'highlight.js';

// Комментарии живут в двух форматах: старые — обычный текст/markdown, новые — html из редактора.
// Рендер и проверки собраны здесь, чтобы комментарии к планировке и к замечанию вели себя одинаково.
const md = new MarkdownIt({
  html: false,
  linkify: true,
  typographer: true,
  breaks: true,
  highlight: (str, lang) => {
    try {
      if (lang && hljs.getLanguage(lang)) {
        return `<pre class="hljs-wrapper"><code class="hljs language-${lang}">${
          hljs.highlight(str, { language: lang, ignoreIllegals: true }).value
        }</code></pre>`;
      }
      return `<pre class="hljs-wrapper"><code class="hljs">${
        hljs.highlightAuto(str).value
      }</code></pre>`;
    } catch (e) {
      return `<pre class="hljs-wrapper"><code class="hljs">${md.utils.escapeHtml(str)}</code></pre>`;
    }
  },
});

md.enable(['table']);

const defaultRender = md.renderer.rules.link_open
  || function defaultRenderFunction(tokens, idx, options, env, self) {
    return self.renderToken(tokens, idx, options);
  };

md.renderer.rules.link_open = function linkOpenRule(tokens, idx, options, env, self) {
  const aIndex = tokens[idx].attrIndex('target');
  if (aIndex < 0) {
    tokens[idx].attrPush(['target', '_blank']);
  } else {
    tokens[idx].attrs[aIndex][1] = '_blank';
  }
  const rIndex = tokens[idx].attrIndex('rel');
  if (rIndex < 0) {
    tokens[idx].attrPush(['rel', 'noopener noreferrer']);
  } else {
    tokens[idx].attrs[rIndex][1] = 'noopener noreferrer';
  }
  return defaultRender(tokens, idx, options, env, self);
};

const parseText = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const sanitizeRichText = (html) => DOMPurify.sanitize(html ?? '');

export const renderRichText = (rawText) => {
  const parsed = parseText(rawText);
  if (!parsed) return '';

  if (/<[a-z][\s\S]*>/i.test(parsed)) {
    return sanitizeRichText(parsed);
  }

  try {
    return sanitizeRichText(md.render(parsed));
  } catch (error) {
    console.error('Error rendering markdown:', error);
    return sanitizeRichText(parsed);
  }
};

// Документ из DOMParser инертен: картинки в нём не грузятся, скрипты не выполняются.
const toDocument = (html) => new DOMParser().parseFromString(html, 'text/html');

/** Пустой редактор отдаёт `<p><br></p>`, поэтому судим по тексту и вложенным картинкам. */
export const isRichTextEmpty = (value) => {
  const html = renderRichText(value);
  if (!html) return true;

  const { body } = toDocument(html);
  if (body.querySelector('img')) return false;

  return body.textContent.trim() === '';
};
